import { useState } from "react";
import {
  Link2,
  Mail,
  Copy,
  Check,
  RefreshCw,
  Send,
  Loader2,
  ExternalLink,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface SubcontractorInviteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subcontractor: {
    id: string;
    company_name: string;
    contact_email: string | null;
    access_token: string | null;
    invitation_sent_at: string | null;
  };
  onRefresh: () => void;
}

const SubcontractorInviteDialog = ({
  open,
  onOpenChange,
  subcontractor,
  onRefresh,
}: SubcontractorInviteDialogProps) => {
  const [copied, setCopied] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const portalUrl = subcontractor.access_token
    ? `${window.location.origin}/portal/${subcontractor.access_token}`
    : null;

  const handleCopyLink = async () => {
    if (!portalUrl) return;

    try {
      await navigator.clipboard.writeText(portalUrl);
      setCopied(true);
      toast({ title: "Lien copié dans le presse-papiers" });
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de copier le lien",
        variant: "destructive",
      });
    }
  };

  const handleRegenerateToken = async () => {
    try {
      setRegenerating(true);
      const { error } = await supabase.rpc("regenerate_subcontractor_token", {
        p_subcontractor_id: subcontractor.id,
      });

      if (error) throw error;

      toast({ title: "Nouveau lien généré" });
      onRefresh();
    } catch (error: any) {
      console.error("Regenerate error:", error);
      toast({
        title: "Erreur",
        description: "Impossible de régénérer le lien",
        variant: "destructive",
      });
    } finally {
      setRegenerating(false);
    }
  };

  const handleSendEmail = async () => {
    if (!subcontractor.contact_email) {
      toast({
        title: "Email manquant",
        description: "Ce prestataire n'a pas d'adresse email configurée",
        variant: "destructive",
      });
      return;
    }

    try {
      setSendingEmail(true);

      const { error } = await supabase.functions.invoke("send-subcontractor-invite", {
        body: {
          subcontractor_id: subcontractor.id,
          email: subcontractor.contact_email,
          company_name: subcontractor.company_name,
          portal_url: portalUrl,
        },
      });

      if (error) throw error;

      // Update invitation_sent_at
      await supabase
        .from("subcontractors")
        .update({ invitation_sent_at: new Date().toISOString() })
        .eq("id", subcontractor.id);

      toast({
        title: "Invitation envoyée",
        description: `Un email a été envoyé à ${subcontractor.contact_email}`,
      });
      onRefresh();
    } catch (error: any) {
      console.error("Send email error:", error);
      toast({
        title: "Erreur d'envoi",
        description: error.message || "Impossible d'envoyer l'email",
        variant: "destructive",
      });
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="w-5 h-5 text-primary" />
            Inviter {subcontractor.company_name}
          </DialogTitle>
          <DialogDescription>
            Partagez ce lien avec votre prestataire pour qu'il puisse déposer ses documents
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Link Section */}
          <div className="space-y-3">
            <Label>Lien d'accès au portail</Label>
            <div className="flex gap-2">
              <Input
                value={portalUrl || "Génération..."}
                readOnly
                className="font-mono text-xs"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={handleCopyLink}
                disabled={!portalUrl}
              >
                {copied ? (
                  <Check className="w-4 h-4 text-emerald-600" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </Button>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleRegenerateToken}
                disabled={regenerating}
              >
                {regenerating ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Générer un nouveau lien
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => portalUrl && window.open(portalUrl, "_blank")}
                disabled={!portalUrl}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Tester
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              ⚠️ Générer un nouveau lien invalidera l'ancien. Le prestataire devra utiliser le nouveau.
            </p>
          </div>

          <Separator />

          {/* Email Section */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Envoyer par email
            </Label>
            
            {subcontractor.contact_email ? (
              <>
                <div className="flex items-center gap-2">
                  <Input
                    value={subcontractor.contact_email}
                    readOnly
                    className="flex-1"
                  />
                  <Button onClick={handleSendEmail} disabled={sendingEmail}>
                    {sendingEmail ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Envoyer
                  </Button>
                </div>
                {subcontractor.invitation_sent_at && (
                  <p className="text-xs text-muted-foreground">
                    Dernière invitation envoyée le{" "}
                    {new Date(subcontractor.invitation_sent_at).toLocaleDateString("fr-FR", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </p>
                )}
              </>
            ) : (
              <p className="text-sm text-amber-600 bg-amber-500/10 p-3 rounded-lg">
                Aucune adresse email configurée pour ce prestataire. 
                Modifiez sa fiche pour ajouter un email.
              </p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SubcontractorInviteDialog;
